/**
 * Support Inbox — one thread (design 2026-09-26, section 6.4).
 *
 *   GET    /v1/admin/support-inbox/threads/{uuid}                support_inbox.read
 *   POST   /v1/admin/support-inbox/threads/{uuid}/reply          support_inbox.write
 *   PATCH  /v1/admin/support-inbox/threads/{uuid}                support_inbox.write
 *   DELETE /v1/admin/support-inbox/threads/{uuid}                support_inbox.delete
 *   GET    /v1/admin/support-inbox/attachments/{uuid}/download   support_inbox.read
 *   GET    /v1/admin/support-inbox/messages/{uuid}/raw           support_inbox.read
 *
 * Markup only; the rules are in `useSupportThreadPage.ts` and `data.ts`.
 *
 * 🔴 TEXT ONLY. A message body is rendered as a React text child inside a
 * `whitespace-pre-wrap` block and nothing else — no raw-HTML injection,
 * no iframe, no remote image, so the panel's CSP (`default-src 'none'`, no
 * `frame-src`) is untouched. When the message also carried HTML the page says
 * so and opens the raw `.eml` in a new tab through a presigned URL.
 *
 * No modal, no browser confirm dialog: the erase control confirms INLINE, because a
 * browser dialog blocks the UI battery's automation and a fixed overlay is
 * the shape `admin-modals-constrain-height` polices.
 */
import { Link, Navigate, useParams } from 'react-router-dom';
import { ArrowLeft, Download, FileText, Mail, RefreshCw, Trash2 } from 'lucide-react';
import { Skeleton } from '@/components/ui/Skeleton';
import { resolveUserDisplayName } from '@/lib/utils';
import { toast } from '@/hooks/useToast';
import {
  COPY,
  DELIVERY_LABELS,
  DELIVERY_PILL_CLASSES,
  GROUP_LABELS,
  HTML_PRESENT_LINE,
  SENDER_NOT_VERIFIED_CHIP,
  STATUS_LABELS,
  STATUS_PILL_CLASSES,
  VERDICT_CHIP_CLASSES,
  formatBytes,
  formatWhen,
  mailboxAddress,
  ownCopyLine,
  senderLabel,
  statusChoicesFor,
  verdictChips,
  type AttachmentView,
  type SupportMessageRow,
  type ThreadStatus,
} from './data';
import { useSupportThreadPage } from './useSupportThreadPage';

function Pill({ className, children }: { className: string; children: React.ReactNode }) {
  return (
    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${className}`}>
      {children}
    </span>
  );
}

function MessageCard({
  message,
  attachments,
  onOpenRaw,
  onDownload,
}: {
  message: SupportMessageRow;
  attachments: AttachmentView[];
  onOpenRaw: (messageId: string) => void;
  onDownload: (attachmentId: string) => void;
}) {
  const outbound = message.direction === 'outbound';
  const chips = verdictChips(message.verdicts);
  return (
    <article
      className={`rounded-lg border bg-card p-4 ${outbound ? 'border-primary/40' : ''}`}
      aria-label={outbound ? 'Outbound message' : 'Inbound message'}
    >
      <header className="flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0 text-sm">
          <div className="font-medium break-all">
            {outbound ? 'To ' : 'From '}
            {outbound
              ? message.to_addresses.join(', ')
              : senderLabel(message.from_address, message.from_name)}
          </div>
          {outbound && (
            <div className="text-xs text-muted-foreground break-all">From {message.from_address}</div>
          )}
          {message.cc_addresses.length > 0 && (
            <div className="text-xs text-muted-foreground break-all">Cc {message.cc_addresses.join(', ')}</div>
          )}
        </div>
        <div className="flex flex-wrap items-center gap-1">
          {outbound && message.delivery && (
            <Pill className={DELIVERY_PILL_CLASSES[message.delivery]}>{DELIVERY_LABELS[message.delivery]}</Pill>
          )}
          {message.is_machine && (
            <Pill className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">machine</Pill>
          )}
          {chips.map((c) => (
            <Pill key={c.key} className={VERDICT_CHIP_CLASSES[c.tone]}>
              {c.label}
            </Pill>
          ))}
          <span className="text-xs text-muted-foreground">{formatWhen(message.occurred_at)}</span>
        </div>
      </header>

      {message.parse_error && (
        <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">{COPY.parseError}</p>
      )}

      {/* TEXT ONLY — a React text child. Never HTML. */}
      <div className="mt-3 whitespace-pre-wrap break-words text-sm">{message.text_body ?? ''}</div>

      {message.text_truncated && (
        <p className="mt-2 text-xs text-muted-foreground">{COPY.textTruncated}</p>
      )}

      {message.html_present && message.has_raw && (
        <p className="mt-2 text-xs text-muted-foreground">
          {HTML_PRESENT_LINE}
          {' — '}
          <button
            type="button"
            onClick={() => onOpenRaw(message.id)}
            className="inline-flex items-center gap-1 text-primary hover:underline"
          >
            <FileText className="h-3.5 w-3.5" />
            Download original (.eml)
          </button>
        </p>
      )}

      {attachments.length > 0 && (
        <ul className="mt-3 space-y-1 border-t pt-3">
          {attachments.map((a) => (
            <li key={a.id} className="flex flex-wrap items-center justify-between gap-2 text-sm">
              <span className="min-w-0 break-all">
                {a.filename?.trim() || '(unnamed attachment)'}
                <span className="ml-2 text-xs text-muted-foreground">
                  {a.content_type} · {formatBytes(a.size_bytes)}
                  {a.is_inline ? ' · inline' : ''}
                </span>
              </span>
              {a.downloadAllowed ? (
                <button
                  type="button"
                  onClick={() => onDownload(a.id)}
                  className="inline-flex items-center gap-1 rounded-lg border px-2 py-1 text-xs hover:bg-muted"
                  aria-label={`Download ${a.filename ?? 'attachment'}`}
                >
                  <Download className="h-3.5 w-3.5" />
                  Download
                </button>
              ) : (
                <span className="text-xs text-muted-foreground">not downloadable</span>
              )}
            </li>
          ))}
        </ul>
      )}
    </article>
  );
}

export function SupportThreadPage() {
  const { threadId } = useParams<{ threadId: string }>();
  const c = useSupportThreadPage(threadId, {
    onError: (message) => toast({ title: 'Not done', description: message, variant: 'destructive' }),
  });

  if (c.deleted) return <Navigate to="/support-inbox" replace />;

  const attachmentsByMessage = new Map<string, AttachmentView[]>();
  for (const a of c.attachments) {
    const list = attachmentsByMessage.get(a.message_id) ?? [];
    list.push(a);
    attachmentsByMessage.set(a.message_id, list);
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <Link
            to="/support-inbox"
            className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Support Inbox
          </Link>
          <h2 className="mt-1 text-2xl font-bold break-words">
            {c.thread ? c.thread.subject?.trim() || '(no subject)' : 'Thread'}
          </h2>
          {c.thread && (
            <p className="text-muted-foreground break-all">
              {mailboxAddress(c.thread)} · {GROUP_LABELS[c.thread.group]} · recipients{' '}
              {c.thread.recipients.join(', ') || '—'}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={c.refresh}
          disabled={c.loading}
          className="inline-flex items-center gap-1 text-xs text-muted-foreground transition-colors hover:text-foreground disabled:opacity-50"
          aria-label="Refresh"
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </button>
      </div>

      {c.accessDenied ? (
        <div className="rounded-lg border bg-card p-8 text-center">
          <h3 className="mb-2 text-lg font-semibold">Access denied</h3>
          <p className="text-muted-foreground">{COPY.accessDenied}</p>
        </div>
      ) : c.notFound ? (
        <div className="rounded-lg border bg-card p-8 text-center">
          <h3 className="mb-2 text-lg font-semibold">Not found</h3>
          <p className="text-muted-foreground">{COPY.notFound}</p>
        </div>
      ) : c.loading && !c.thread ? (
        <div className="space-y-3">
          <Skeleton className="h-24 w-full" />
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : c.thread ? (
        <>
          {/* Header card: member, status, assignee, chips */}
          <div className="grid gap-4 rounded-lg border bg-card p-4 md:grid-cols-3">
            <div className="text-sm">
              <div className="text-xs font-medium text-muted-foreground">Sender</div>
              <div className="break-all">{senderLabel(c.thread.first_from_address, c.thread.first_from_name)}</div>
              <div className="mt-1 flex flex-wrap gap-1">
                {!c.thread.sender_verified && !c.thread.is_machine && (
                  <Pill className="bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-400">
                    {SENDER_NOT_VERIFIED_CHIP}
                  </Pill>
                )}
                {c.thread.virus_flagged && (
                  <Pill className="bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-400">virus flagged</Pill>
                )}
                {c.thread.is_machine && (
                  <Pill className="bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300">machine</Pill>
                )}
              </div>
            </div>

            <div className="text-sm">
              <div className="text-xs font-medium text-muted-foreground">Member</div>
              {c.member ? (
                <div>
                  <Link to={`/users/${c.member.id}`} className="text-primary hover:underline">
                    {resolveUserDisplayName(c.member)}
                  </Link>
                  {c.member.email && (
                    <div className="text-xs text-muted-foreground break-all">{c.member.email}</div>
                  )}
                </div>
              ) : (
                <div className="text-muted-foreground">Not linked to a member</div>
              )}
            </div>

            <div className="space-y-2 text-sm">
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="support-thread-status">
                  Status
                </label>
                {c.canWrite ? (
                  <select
                    id="support-thread-status"
                    value={c.thread.status}
                    onChange={(e) => void c.setStatus(e.target.value as ThreadStatus)}
                    disabled={c.updating}
                    aria-label="Change status"
                    className="h-8 rounded-lg border bg-background px-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                  >
                    {statusChoicesFor(c.thread).map((s) => (
                      <option key={s} value={s}>
                        {STATUS_LABELS[s]}
                      </option>
                    ))}
                  </select>
                ) : (
                  <Pill className={STATUS_PILL_CLASSES[c.thread.status]}>{STATUS_LABELS[c.thread.status]}</Pill>
                )}
              </div>
              <div className="flex items-center gap-2">
                <label className="text-xs font-medium text-muted-foreground" htmlFor="support-thread-assignee">
                  Assignee
                </label>
                {c.canWrite ? (
                  <select
                    id="support-thread-assignee"
                    value={c.thread.assigned_admin_id ?? ''}
                    onChange={(e) => void c.assign(e.target.value === '' ? null : e.target.value)}
                    disabled={c.updating}
                    aria-label="Change assignee"
                    className="h-8 min-w-0 rounded-lg border bg-background px-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
                  >
                    <option value="">Unassigned</option>
                    {c.assigneeOptions.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  <span>{c.thread.assigned_admin_display_name ?? 'Unassigned'}</span>
                )}
              </div>
              <div className="text-xs text-muted-foreground">
                {c.thread.message_count} {c.thread.message_count === 1 ? 'message' : 'messages'} · last activity{' '}
                {formatWhen(c.thread.last_message_at)}
              </div>
            </div>
          </div>

          {/* Messages, in order. An own copy — our reply received back through the inbox — is ONE muted line, never a card. */}
          <div className="space-y-3">
            {c.messages.map((m) =>
              m.own_copy ? (
                <p key={m.id} className="text-xs text-muted-foreground" aria-label="Own copy">
                  {ownCopyLine(m, c.thread!)}
                </p>
              ) : (
                <MessageCard
                  key={m.id}
                  message={m}
                  attachments={attachmentsByMessage.get(m.id) ?? []}
                  onOpenRaw={(id) => void c.openRaw(id)}
                  onDownload={(id) => void c.downloadAttachment(id)}
                />
              ),
            )}
          </div>

          {/* The collision guard: the thread moved after the composer rendered — read it, then reply */}
          {c.moved && (
            <div
              className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-amber-500/50 bg-amber-100/40 p-3 text-sm dark:bg-amber-950/40"
              role="alert"
            >
              <span>{c.error}</span>
              <button
                type="button"
                onClick={c.refresh}
                disabled={c.loading}
                className="inline-flex items-center gap-1 rounded-lg border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
              >
                <RefreshCw className="h-3.5 w-3.5" />
                {COPY.refresh}
              </button>
            </div>
          )}

          {/* Composer — hidden when the thread cannot be answered */}
          {c.canReply && c.replyToAddress ? (
            <form
              onSubmit={(e) => void c.submitReply(e)}
              className="space-y-3 rounded-lg border bg-card p-4"
              aria-label="Reply"
            >
              <div className="flex items-center gap-2 text-sm">
                <Mail className="h-4 w-4 text-muted-foreground" />
                <span>
                  Reply to <span className="font-medium break-all">{c.replyToAddress}</span>
                </span>
              </div>
              <textarea
                value={c.composerText}
                onChange={(e) => c.setComposerText(e.target.value)}
                disabled={c.sending}
                rows={6}
                maxLength={20000}
                aria-label="Reply text"
                placeholder="Write the reply as plain text."
                className="w-full rounded-lg border bg-background px-3 py-2 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary disabled:opacity-50"
              />
              <div className="flex flex-wrap items-center justify-between gap-2">
                <span className="text-xs text-muted-foreground">
                  Sent as {mailboxAddress(c.thread)}. Text only; the thread moves to Pending.
                </span>
                <button
                  type="submit"
                  disabled={c.sending || c.composerText.trim().length === 0}
                  className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {c.sending ? 'Sending…' : 'Send reply'}
                </button>
              </div>
            </form>
          ) : (
            c.canWrite &&
            !c.moved && (
              <div className="flex flex-wrap items-center justify-between gap-3">
                <p className="text-sm text-muted-foreground">
                  {c.thread.is_machine ? COPY.machineThread : COPY.noReplyTarget}
                </p>
                {/* Machine → human: a header-only verdict an operator may overrule; a DSN is refused by the server */}
                {c.canReopenMachine && (
                  <button
                    type="button"
                    onClick={() => void c.reopenAsHuman()}
                    disabled={c.updating}
                    className="rounded-lg border px-3 py-1.5 text-sm hover:bg-muted disabled:opacity-50"
                  >
                    {COPY.reopenMachine}
                  </button>
                )}
              </div>
            )
          )}

          {/* Erase — support_inbox.delete only, confirmed inline */}
          {c.canDelete && (
            <div className="rounded-lg border border-destructive/40 bg-card p-4">
              {c.deleteArmed ? (
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <p className="text-sm">{COPY.deleteConfirm}</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={c.disarmDelete}
                      disabled={c.deleting}
                      className="rounded-lg border px-3 py-2 text-sm hover:bg-muted disabled:opacity-50"
                    >
                      {COPY.deleteCancel}
                    </button>
                    <button
                      type="button"
                      onClick={() => void c.deleteThread()}
                      disabled={c.deleting}
                      className="rounded-lg bg-destructive px-3 py-2 text-sm font-medium text-destructive-foreground hover:bg-destructive/90 disabled:opacity-50"
                    >
                      {c.deleting ? 'Erasing…' : COPY.deleteConfirmControl}
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={c.armDelete}
                  className="inline-flex items-center gap-1 text-sm text-destructive hover:underline"
                >
                  <Trash2 className="h-4 w-4" />
                  {COPY.deleteControl}
                </button>
              )}
            </div>
          )}
        </>
      ) : null}
    </div>
  );
}

export default SupportThreadPage;
